import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import chroma from "chroma-js";
import countries from "i18n-iso-countries";
import MenuItem from "@material-ui/core/MenuItem";
import World from "../../../../assets/svg-maps/world.robinson";
import "flag-icon-css/css/flag-icon.min.css";

import { formatDate } from "../../../../utilities";
import { colors, Select, Slider, LegendColor } from "../../../../theme";
import Map from "../../../dataviz/DataMap";
import timeline from "./assets/timeline.json";
import policies from "./assets/policies.json";

countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));

const Container = styled.div`
  margin: 60px 0 70px;

  @media (max-width: 1599px) {
    margin: 50px 0 60px;
  }

  @media (max-width: 767px) {
    margin: 40px 0 50px;
  }
`;

const Policy = styled.div`
  align-items: flex-end;
  display: flex;
  margin-bottom: 40px;

  > p {
    border-left: 3px solid ${chroma(colors.purple).alpha(0.3).css()};
    color: ${colors.textColor};
    line-height: 2rem;
    margin: 0 25px 0 0;
    padding-left: 12px;
    width: calc(100% - 425px);

    i {
      color: ${colors.green};
    }
  }

  > div {
    width: 400px;
  }

  @media (max-width: 1599px) {
    > p {
      width: calc(100% - 365px);
    }

    > div {
      width: 340px;
    }
  }

  @media (max-width: 1023px) {
    > p {
      width: calc(100% - 325px);
    }

    > div {
      width: 300px;
    }
  }

  @media (max-width: 767px) {
    display: block;
    margin-bottom: 30px;

    > p {
      margin-bottom: 20px;
      width: 100%;
    }

    > div {
      width: 100%;
    }
  }
`;

const Legend = styled.div`
  color: ${colors.textColor};
  margin-bottom: 25px;
  text-align: right;

  ul {
    display: inline-block;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    font-size: 1.2rem;
    display: inline-block;
    line-height: 2.4rem;
    margin-left: 20px;

    :first-child {
      margin: 0;
    }

    @media (max-width: 767px) {
      font-size: 1.1rem;
      margin-left: 10px;
    }
  }
`;

const Time = styled.div`
  display: flex;
  margin-bottom: 50px;

  @media (max-width: 767px) {
    margin-bottom: 40px;
  }

  button {
    color: ${colors.green};
    font-size: 2.4rem;
    margin-right: 40px;

    .fa-pause {
      color: ${colors.primaryColor};
    }
  }
`;

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
`;
const Note = styled.a`
  animation: ${appear} 0.3s linear;
  background: ${chroma(colors.textColor).brighten(3.6)};
  border-radius: 4px;
  color: ${colors.textColor} !important;
  display: block;
  font-weight: 500;
  padding: 14px;
  text-align: center;
  text-decoration: none;
  transition: 0.3s;

  :hover {
    color: ${colors.primaryColor} !important;
  }

  i {
    color: ${colors.primaryColor};
    margin-right: 6px;
  }

  strong {
    font-weight: 600;
  }
`;

const dates = Object.keys(timeline);

function getLocationColor(val) {
  switch (val) {
    case 1:
      return colors.green;
    case 0:
      return colors.blueGray;
    default:
      return chroma(colors.borderColor).brighten(0.6);
  }
}

function CovidWorldResponseTimeline() {
  const [policy, setPolicy] = React.useState("School_closing");
  const [dateIndex, setDateIndex] = useState(0);
  const [isAnimationStarted, setIsAnimationStarted] = useState(false);
  const animationTimeoutRef = useRef(null);
  const mapRef = useRef(null);

  const selectedDate = timeline[dates[dateIndex]];
  const event = selectedDate.event;

  useEffect(() => () => clearTimeout(animationTimeoutRef.current), []);

  useEffect(() => {
    const data = selectedDate.policies[policy];

    Object.keys(data).forEach((countryId) => {
      const el = mapRef.current.querySelector(`path[id="${countryId}"]`);
      if (el) {
        el.style.fill = getLocationColor(data[countryId]);
      }
    });
  }, [selectedDate, policy]);

  function getTooltipContent(countryId) {
    const confirmedDeaths = selectedDate.confirmedDeaths;

    return {
      title: (
        <>
          <span className={`flag-icon flag-icon-${countryId.toLowerCase()}`} />
          {countries.getName(countryId, "fr")}
        </>
      ),
      mainContent: confirmedDeaths[countryId] !== undefined &&
        confirmedDeaths[countryId] !== null && (
          <>
            Décès : <span>{confirmedDeaths[countryId]}</span>
          </>
        ),
    };
  }

  function toggleAnimation() {
    setIsAnimationStarted((flag) => {
      if (!flag) {
        loopAnimation();
      } else {
        clearTimeout(animationTimeoutRef.current);
      }

      return !flag;
    });
  }

  function loopAnimation() {
    setDateIndex((index) => {
      const nextIndex = index >= dates.length - 1 ? 0 : index + 1;

      if (index < dates.length - 2 || nextIndex === 0) {
        let delay = 100;

        if (timeline[dates[nextIndex]].event) {
          delay = 2000;
        }

        animationTimeoutRef.current = setTimeout(loopAnimation, delay);
      } else {
        setIsAnimationStarted(false);
      }

      return nextIndex;
    });
  }

  const handlePolicyChange = (e) => {
    if (isAnimationStarted) {
      toggleAnimation();
    }
    setPolicy(e.target.value);
  };

  return (
    <Container>
      <Policy>
        <p>
          Choisissez une mesure de lutte contre le Covid-19 et{" "}
          <i className="fas fa-play fa-fw" /> lancez l'animation
        </p>
        <Select value={policy} onChange={handlePolicyChange}>
          {policies.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Policy>

      <Map
        map={World}
        containerRef={mapRef}
        getTooltipContent={getTooltipContent}
        forceTooltipUpdate={dateIndex}
      />

      <Legend>
        <ul>
          <li>
            <LegendColor color={getLocationColor(0)} /> Mesure non adoptée
          </li>
          <li>
            <LegendColor color={getLocationColor(1)} /> Mesure adoptée
          </li>
          <li>
            <LegendColor color={getLocationColor(-1)} /> Non référencé
          </li>
        </ul>
      </Legend>

      <Time>
        <button onClick={toggleAnimation}>
          {isAnimationStarted ? (
            <i className="fas fa-pause" />
          ) : (
            <i className="fas fa-play" />
          )}
        </button>
        <Slider
          min={0}
          max={dates.length - 1}
          value={dateIndex}
          valueLabelDisplay="on"
          valueLabelFormat={(index) => formatDate(dates[index], "d MMMM")}
          onChange={(e, val) => setDateIndex(val)}
        />
      </Time>

      {event && (
        <Note
          href={event.source}
          target="_blank"
          onClick={() => isAnimationStarted && toggleAnimation()}
        >
          <i className="fas fa-external-link" />{" "}
          <strong>{formatDate(dates[dateIndex], "d MMMM yyyy")}</strong> :{" "}
          {event.text}
        </Note>
      )}
    </Container>
  );
}

export default CovidWorldResponseTimeline;
